// @flow

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Layout from '../../../../layouts/default-logged-in';
import SettingsPageHeader from '../../settings-page-header';
import { Wrapper as SharedWrapper } from '../../settings-page-styles';
import type { BrowserHistory } from 'history/createBrowserHistory';
import * as RecordingModel from '../../../../../models/recording';
import * as LocationModel from '../../../../../models/location';

import styled from 'styled-components';
import _ from 'lodash';

import LocationTable from '../shared/components/location-table';
import { fetchRecordingUptimeStatus } from '../../../../../actions/uptime';
import { errorToast } from '../../../../../utils/toaster';
import { Button, InputGroup, ProgressBar } from '@blueprintjs/core';

import {
  TAG_TYPES,
  TaggingErrorBoundary,
  TagFilterBar,
  BulkBar,
  BulkTagPickerDialog,
  ManageTagsModal,
  RowTagsCell,
  tagFilterPredicate,
  useTagPermissions,
} from '../../../../location-tagging';

// Locations per status query. Batches are sent in table display order so
// rows fill from the top down.
const STATUS_BATCH_SIZE = 25;
// Status queries this page keeps in flight. The shared query worker allows
// 12; this page uses fewer so it cannot starve the next route.
const STATUS_MAX_IN_FLIGHT = 4;

const LoadingText = styled.div`
  text-align: center;
  padding: 2em 0 2em;
  color: var(--mid-grey);
`;

const LoadingBar = styled(ProgressBar)`
  margin: 0 auto 2em;
  max-width: 500px;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  flex-wrap: wrap;
`;

// Extend the shared <Wrapper> (which is just `flex: 1`) into a flex column
// that allows shrinking. The flex chain Content→Padding→Wrapper needs
// `min-height: 0` at every level to give children a constrained height —
// without it, Wrapper expands to its content size and the page-level
// scroll (Content) activates instead of our internal TableScrollArea.
const Wrapper = styled(SharedWrapper)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Toolbar = styled.div`
  flex: 0 0 auto;
  background: var(--main-background, white);
`;

// The only scrolling region on the page. Sticky <thead> inside the table
// pins to the top of this container (its closest scrolling ancestor),
// keeping the column headers visible without overlapping the toolbar.
const TableScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

// Sized to the table's intrinsic width (or 100% of the scroll area, whichever
// is larger). The white background must follow the table's actual width
// because Blueprint's bp3-html-table-striped applies a 15%-opacity overlay
// to <td> cells — designed to sit on white. If the table extends past this
// surface, the overlay sits on the page background instead, going darker.
//
// `overflow: clip` clips corner cells to the rounded edge without creating
// a CSS scroll container — unlike `overflow: hidden`, which would become
// the sticky <th> binding ancestor and break header pinning.
const TableSurface = styled.div`
  min-width: 100%;
  width: max-content;
  background: white;
  border-radius: var(--widget-border-radius, 12px);
  overflow: clip;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
`;

const Subtitle = styled.span`
  color: #5c7080;
  font-size: 13px;
`;

const HeaderSpacer = styled.span`
  flex: 1;
`;

const SearchBox = styled(InputGroup)`
  width: 240px;
`;

type EntityTagT = {
  id: string,
  organisation: string,
  targetCollection: 'locations',
  tagType: string,
  tagValue: string,
};

type Props = {
  history: BrowserHistory,
  locations: LocationModel.t[],
  recordings: RecordingModel.t[],
  entityTags: EntityTagT[],
  fetchTags: () => Promise<*>,
  createTag: (tagType: string, tagValue: string) => Promise<*>,
  updateTag: (tagId: string, tagValue: string) => Promise<*>,
  deleteTag: (tagId: string) => Promise<*>,
  assignTagToLocation: (locationId: string, tag: EntityTagT) => Promise<*>,
  removeTagFromLocation: (locationId: string, tagId: string) => Promise<*>,
  bulkAssignTags: (locationIds: string[], tag: EntityTagT) => Promise<*>,
  bulkRemoveTags: (locationIds: string[], tag: EntityTagT) => Promise<*>,
  hydrateLocationTags: () => Promise<*>,
};

// A location row carries its own status state: `statusLoaded` gates the
// spinner, `statusError` marks a batch that failed, and the counts come from
// the status query.
type LocationWithStatusT = LocationModel.t & {
  statusLoaded?: boolean,
  statusError?: boolean,
  recordingsUp?: number,
  recordingsTotal?: number,
  recordingPercent?: number,
};

// Map<locationId, recordingId[]> from the redux recordings list.
const groupRecordingIdsByLocation = (recordings) => {
  const byLocation = new Map();
  recordings.forEach((r) => {
    const locationId = r.location && r.location.id;
    if (!locationId) return;
    const ids = byLocation.get(locationId);
    if (ids) ids.push(r.id);
    else byLocation.set(locationId, [r.id]);
  });
  return byLocation;
};

// Merge the rows of one batch into the locations that batch covers. Locations
// outside the batch are returned untouched, so batches cannot overwrite each
// other.
const mergeUptimeIntoLocations = (
  locationData,
  batchLocationIds,
  rows,
  failed,
) =>
  locationData.map((location) => {
    if (!batchLocationIds.has(location.id)) return location;
    if (failed) {
      return { ...location, statusLoaded: true, statusError: true };
    }
    const matched = rows.filter((r) => r.location_id === location.id);
    const recordingsUp = matched.filter((r) => r.status === 'online').length;
    const recordingsTotal = matched.length;
    return {
      ...location,
      statusLoaded: true,
      statusError: false,
      recordingsUp,
      recordingsTotal,
      recordingPercent:
        recordingsTotal === 0 ? 0 : recordingsUp / recordingsTotal,
    };
  });

const ManageLocations = ({
  history,
  locations,
  recordings,
  entityTags,
  fetchTags,
  createTag,
  updateTag,
  deleteTag,
  assignTagToLocation,
  removeTagFromLocation,
  bulkAssignTags,
  bulkRemoveTags,
}: Props) => {
  const [locationData, setLocationData] = useState<LocationWithStatusT[]>(
    locations.map((l) => ({ ...l })),
  );
  const [progress, setProgress] = useState(0.1);

  // The status queue is owned by the page. `cancelledRef` stops it when the
  // user leaves, `startedRef` keeps it to one run, and `errorToastShownRef`
  // holds the failure toast to one per page load.
  const cancelledRef = useRef(false);
  const startedRef = useRef(false);
  const errorToastShownRef = useRef(false);

  const permissions = useTagPermissions();

  const [manageOpen, setManageOpen] = useState(false);
  const [filters, setFilters] = useState<EntityTagT[]>([]);
  const [nameSearch, setNameSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkPicker, setBulkPicker] = useState<?'add' | 'remove'>(null);

  // Re-merge tag IDs onto locationData whenever the redux locations change
  // (so newly hydrated tags show up in the table without a full re-fetch).
  useEffect(() => {
    setLocationData((prev) => {
      if (!prev) return prev;
      const byId: { [string]: any } = {};
      locations.forEach((l) => {
        byId[l.id] = l;
      });
      return prev.map((l) => {
        const fresh = byId[l.id];
        if (!fresh) return l;
        return { ...l, tags: fresh.tags };
      });
    });
  }, [locations]);

  const runStatusQueue = async (allLocations: LocationWithStatusT[]) => {
    // Same comparator as the table's default Name sort, so batch 1 is the top
    // rows of the table.
    const orderedLocations = _.orderBy(allLocations, ['name'], ['asc']);
    const recordingsByLocation = groupRecordingIdsByLocation(recordings);
    const hasRecordings = (l) =>
      (recordingsByLocation.get(l.id) || []).length > 0;
    const withRecordings = orderedLocations.filter(hasRecordings);
    const withoutIds = new Set(
      orderedLocations.filter((l) => !hasRecordings(l)).map((l) => l.id),
    );

    // Nothing to fetch for these. Mark them loaded up front.
    if (withoutIds.size > 0) {
      setLocationData((prev) =>
        mergeUptimeIntoLocations(prev, withoutIds, [], false),
      );
    }

    const batches = _.chunk(withRecordings, STATUS_BATCH_SIZE).map((locs) => ({
      locationIds: new Set(locs.map((l) => l.id)),
      recordingIds: locs.flatMap((l) => recordingsByLocation.get(l.id)),
    }));

    let next = 0;
    const worker = async () => {
      while (!cancelledRef.current && next < batches.length) {
        const batch = batches[next++];
        const rows = await fetchRecordingUptimeStatus(batch.recordingIds);
        if (cancelledRef.current) return;
        // A failed query resolves with undefined or with the server's error
        // body, so an array is the only signal of success. An empty array is a
        // valid result.
        const ok = Array.isArray(rows);
        const batchRows = Array.isArray(rows) ? rows : [];
        if (!ok && !errorToastShownRef.current) {
          errorToastShownRef.current = true;
          errorToast({
            message: 'Could not load status for some locations.',
            timeout: 3000,
          });
        }
        setLocationData((prev) =>
          mergeUptimeIntoLocations(prev, batch.locationIds, batchRows, !ok),
        );
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(STATUS_MAX_IN_FLIGHT, batches.length) }, worker),
    );
  };

  useEffect(() => {
    startTimer(progress);
    // hydrateLocationTags only seeds entityTags from tags currently assigned
    // to a location — unassigned tags would be missing from the pickers.
    // fetchTags pulls the full org tag list so pickers always have every value.
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (startedRef.current || !locationData || locationData.length === 0) return;
    startedRef.current = true;
    runStatusQueue(locationData).catch((e) => {
      console.error(e);
      if (cancelledRef.current) return;
      // Mark every row that is still loading as failed so no spinner runs
      // forever.
      setLocationData((prev) =>
        prev.map((l) =>
          l.statusLoaded ? l : { ...l, statusLoaded: true, statusError: true },
        ),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationData]);

  const startTimer = (newProgress) => {
    if (!locationData && newProgress <= 1) {
      setProgress(newProgress);
      setTimeout(() => {
        startTimer(newProgress + Math.random() / 10);
      }, Math.random() * 1000);
    } else {
      setProgress(1);
    }
  };

  // Drop filters that reference tags no longer in the store
  useEffect(() => {
    const tagIds = new Set(entityTags.map((t) => t.id));
    setFilters((prev) => prev.filter((f) => tagIds.has(f.id)));
  }, [entityTags]);

  // Drop selections for locations that no longer exist
  useEffect(() => {
    if (!locationData) return;
    const ids = new Set(locationData.map((l) => l.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [locationData]);

  // Composed predicate: AND of tag filter and case-insensitive substring
  // match on location.name. Selections persist regardless — the predicate
  // controls visibility only, never `selected`.
  const predicate = useMemo(() => {
    const byTag = tagFilterPredicate(filters);
    const trimmed = nameSearch.trim().toLowerCase();
    if (!trimmed) return byTag;
    return (loc) =>
      byTag(loc) && !!loc.name && loc.name.toLowerCase().includes(trimmed);
  }, [filters, nameSearch]);

  const visibleLocations = useMemo(() => {
    if (!locationData) return [];
    return locationData.filter(predicate);
  }, [locationData, predicate]);

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((visibleIds: string[]) => {
    setSelected((prev) => {
      const allSelected = visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    const ids = visibleLocations.map((l) => l.id);
    setSelected(new Set(ids));
  }, [visibleLocations]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const onAddFilter = (tag: EntityTagT) => {
    setFilters((prev) => (prev.find((f) => f.id === tag.id) ? prev : [...prev, tag]));
  };
  const onRemoveFilter = (tag: EntityTagT) => {
    setFilters((prev) => prev.filter((f) => f.id !== tag.id));
  };
  const onClearFilters = () => setFilters([]);

  const handleRowAddTag = (locationId: string, tag: EntityTagT) =>
    assignTagToLocation(locationId, tag);

  const handleRowRemoveTag = (locationId: string, tagId: string) =>
    removeTagFromLocation(locationId, tagId);

  const handleBulkConfirm = async (tag: EntityTagT) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (bulkPicker === 'add') {
      await bulkAssignTags(ids, tag);
    } else if (bulkPicker === 'remove') {
      await bulkRemoveTags(ids, tag);
    }
  };

  const onClickManageTags = () => setManageOpen(true);

  const tagsCount = entityTags.length;
  const locationsCount = (locationData || []).length;
  const statusLoadedCount = (locationData || []).filter(
    (l) => l.statusLoaded,
  ).length;
  const statusPending = locationsCount > 0 && statusLoadedCount < locationsCount;

  return (
    <Layout>
      <SettingsPageHeader history={history} />
      <Wrapper>
        <Toolbar>
            <PageHeaderRow>
              <Title>Manage locations</Title>
              <Subtitle>
                {locationsCount} location{locationsCount === 1 ? '' : 's'} ·{' '}
                {tagsCount} tag{tagsCount === 1 ? '' : 's'} configured
                {statusPending && (
                  <> · Checking status… {statusLoadedCount} / {locationsCount}</>
                )}
              </Subtitle>
              <HeaderSpacer />
              <SearchBox
                leftIcon="search"
                placeholder="Search locations…"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                rightElement={
                  nameSearch ? (
                    <Button
                      minimal
                      icon="cross"
                      onClick={() => setNameSearch('')}
                      title="Clear search"
                    />
                  ) : undefined
                }
              />
              {!permissions.loading && permissions.canManageTagsButton && (
                <Button
                  icon="tag"
                  onClick={onClickManageTags}
                  text="Manage Tags"
                  title="Create, rename, or delete tag types and values for this organisation"
                />
              )}
            </PageHeaderRow>

            {!permissions.loading && (
              <TaggingErrorBoundary>
                <TagFilterBar
                  tagTypes={TAG_TYPES}
                  allTags={entityTags}
                  activeFilters={filters}
                  onAdd={onAddFilter}
                  onRemove={onRemoveFilter}
                  onClear={onClearFilters}
                />
                <BulkBar
                  count={selected.size}
                  total={visibleLocations.length}
                  permissions={permissions}
                  onAddTag={() => setBulkPicker('add')}
                  onRemoveTag={() => setBulkPicker('remove')}
                  onClear={clearSelection}
                  onSelectAll={selectAllVisible}
                />
              </TaggingErrorBoundary>
            )}
          </Toolbar>

          <TableScrollArea>
            <TableSurface>
                {!locationData && progress < 1 && (
                  <>
                    <LoadingText>Fetching location uptime data...</LoadingText>
                    <LoadingBar
                      intent="primary"
                      value={progress}
                      animate={false}
                    />
                  </>
                )}
                {!locationData && progress === 1 && (
                  <>
                    <LoadingText>Processing location uptime data...</LoadingText>
                    <LoadingBar intent="primary" value={1} />
                  </>
                )}
                {!!locationData && (
                  <LocationTable
                    data={locationData}
                    selectable={!permissions.loading}
                    selected={selected}
                    onToggleRow={toggleRow}
                    onToggleAll={toggleAll}
                    tagFilterPredicate={predicate}
                    stickyHeader
                    tagsColumn={
                      permissions.loading
                        ? null
                        : {
                            render: (l) => (
                              <TaggingErrorBoundary fallback={null}>
                                <RowTagsCell
                                  location={l}
                                  permissions={permissions}
                                  tagTypes={TAG_TYPES}
                                  allTags={entityTags}
                                  onAddTag={handleRowAddTag}
                                  onRemoveTag={handleRowRemoveTag}
                                />
                              </TaggingErrorBoundary>
                            ),
                          }
                    }
                  />
                )}
            </TableSurface>
          </TableScrollArea>

        {!permissions.loading && (
          <TaggingErrorBoundary>
            <ManageTagsModal
              isOpen={manageOpen}
              onClose={() => setManageOpen(false)}
              permissions={permissions}
              tagTypes={TAG_TYPES}
              allTags={entityTags}
              locations={locationData || locations}
              onCreate={createTag}
              onUpdate={updateTag}
              onDelete={deleteTag}
              onOpenRefresh={fetchTags}
            />
            <BulkTagPickerDialog
              isOpen={bulkPicker !== null}
              mode={bulkPicker || 'add'}
              selectionCount={selected.size}
              tagTypes={TAG_TYPES}
              allTags={entityTags}
              onClose={() => setBulkPicker(null)}
              onConfirm={handleBulkConfirm}
            />
          </TaggingErrorBoundary>
        )}
      </Wrapper>
    </Layout>
  );
};

export default ManageLocations;
